import { Box, Checkbox, Chip, FormControl, TextField } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import cn from 'classnames';
import React, { forwardRef, useEffect, useState } from 'react';
import { RegisterOptions, UseFormRegister } from 'react-hook-form';

import styles from './styles.module.scss';

interface InputProps {
  wrapperClassName?: string;
  options: { id: string; value: string | number; label: string }[];
  inputClass?: string;
  register?: UseFormRegister<any>;
  registerOptions?: RegisterOptions;
  errors?: { [key: string]: any };
  wrapperStyle?: React.CSSProperties;
  name: string;
  label?: string;
  onChange?: any;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  value?: (string | { id: string; value: string | number; label: string })[];
  required?: boolean;
  hideLabel?: boolean;
  styledTag?: boolean;
  displayCheckbox?: boolean;
}

const MultiSelectInputFreeSolo = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      wrapperClassName,
      options,
      inputClass,
      wrapperStyle,
      name,
      register,
      registerOptions,
      errors,
      label,
      onChange,
      onBlur,
      disabled = false,
      value = [],
      required = false,
      hideLabel = false,
      styledTag = false,
      displayCheckbox = true,
      ...props
    },
    ref,
  ) => {
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
      if (errors && name) {
        const nameArray = name.split('.');
        const error = nameArray.length > 1 ? errors[nameArray[0]]?.[nameArray[1]]?.message : errors[name]?.message;
        setErrorMessage(error || '');
      } else {
        setErrorMessage('');
      }
    }, [errors, name]);

    return (
      <Box className={cn(styles.inputWrapper, wrapperClassName)} style={wrapperStyle}>
        <FormControl sx={{ border: '0', height: '56px', width: '100%' }} error={!!errorMessage}>
          <Autocomplete
            multiple
            freeSolo
            disablePortal
            options={options}
            getOptionLabel={(option) => (typeof option === 'string' ? option : option.label)}
            fullWidth
            value={value.filter((val) => (typeof val === 'string' ? val.trim() !== '' : val && val.label))}
            onChange={(event, newValue) => {
              const mapped = newValue
                .map((item) => (typeof item === 'string' ? item.trim() : item.label))
                .filter((v) => v && v !== '');
              onChange?.(mapped);
            }}
            disabled={disabled}
            ref={ref}
            {...props}
            renderTags={(selected, getTagProps) => {
              return selected.map((option, index) => {
                const lbl = typeof option === 'string' ? option : option.label;
                return styledTag ? (
                  <Chip
                    variant="outlined"
                    color="primary"
                    label={lbl}
                    {...getTagProps({ index })}
                    key={lbl}
                    sx={{
                      borderRadius: '24px',
                      fontWeight: 500,
                      color: '#00A1E0',
                      borderColor: '#00A1E0',
                      '.MuiChip-deleteIcon': {
                        color: '#00A1E0',
                      },
                    }}
                  />
                ) : (
                  lbl
                );
              });
            }}
            renderOption={(optionProps, option, { selected }) => {
              const lbl = typeof option === 'string' ? option : option.label;
              return (
                <li {...optionProps} key={lbl}>
                  {displayCheckbox && <Checkbox checked={selected} />}
                  {lbl}
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                className={cn(styles.input, inputClass)}
                label={hideLabel ? undefined : label}
                InputLabelProps={
                  hideLabel
                    ? { shrink: false }
                    : {
                        shrink: Boolean(value.length) || undefined,
                        sx: {
                          fontSize: '14px',
                          color: 'rgba(23, 36, 51, 0.6)',
                          backgroundColor: 'white',
                          px: 0.3,
                          '&.MuiInputLabel-shrink': {
                            transform: 'translate(6px, -10px) scale(0.75)',
                          },
                          '&::after': required
                            ? {
                                content: '" *"',
                                color: 'red',
                                opacity: 0.5,
                                fontSize: '16px',
                                fontWeight: 'bold',
                              }
                            : {},
                        },
                      }
                }
                sx={{
                  '& .MuiOutlinedInput-notchedOutline legend': {
                    maxWidth: '0.01px !important',
                    transition: 'max-width 0.2s ease-out',
                  },
                  '& .MuiInputLabel-shrink + .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline legend': {
                    maxWidth: '0.01px !important',
                  },
                  '&&&& .MuiOutlinedInput-notchedOutline legend': {
                    maxWidth: '0.01px !important',
                  },
                }}
                error={!!errorMessage}
                helperText={errorMessage}
                {...(register ? register(name, registerOptions) : {})}
              />
            )}
          />
        </FormControl>
      </Box>
    );
  },
);

export default MultiSelectInputFreeSolo;
